import React from 'react';
import { Star } from 'lucide-react';
// Internal component for the review cards
const ReviewCard = ({
  rating,
  text,
  variant,
  className
}: {
  rating: number;
  text: string;
  variant: 'bad' | 'good';
  className?: string;
}) => {
  const isBad = variant === 'bad';
  const colorClass = isBad ? 'text-[#FF6B35]' : 'text-[#4ECDC4]';
  const bgClass = isBad ? 'bg-[#FF6B35]/10 border-[#FF6B35]/30 shadow-[0_4px_20px_rgba(255,107,53,0.15)]' : 'bg-[#4ECDC4]/10 border-[#4ECDC4]/30 shadow-[0_4px_20px_rgba(78,205,196,0.15)]';
  return <div className={`p-3 rounded-lg border backdrop-blur-md w-48 ${bgClass} ${className}`} data-id="element-4">
      <div className="flex gap-1 mb-2" data-id="element-5">
        {[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < rating ? `fill-current ${colorClass}` : 'text-white/20 fill-current'}`} data-id="element-6" />)}
      </div>
      <p className="text-xs text-white/90 font-medium leading-relaxed" data-id="element-7">
        "{text}"
      </p>
    </div>;
};
export function SocialMediaPost() {
  return <div className="w-full max-w-[600px] aspect-square relative overflow-hidden rounded-2xl shadow-2xl bg-[#1a1a2e] font-sans select-none" data-id="element-8">
      {/* LEFT SIDE: WITHOUT WeddingPitch */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_40%,rgba(255,107,53,0.2),transparent_60%)]" style={{
      clipPath: 'polygon(0 0, 55% 0, 45% 100%, 0 100%)'
    }} data-id="element-9">
        {/* Stressed Silhouette */}
        <svg viewBox="0 0 100 100" className="absolute bottom-16 -left-4 w-64 h-64 opacity-10 text-[#FF6B35] fill-current" data-id="element-10">
          <circle cx="35" cy="40" r="12" data-id="element-11" />
          <path d="M35 55 C20 55 5 80 0 100 L70 100 C65 80 50 55 35 55 Z" data-id="element-12" />
          <path d="M10 75 C15 55 25 40 35 40" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" data-id="element-13" />
        </svg>

        <div className="absolute top-8 left-8" data-id="element-14">
          <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-1" data-id="element-15">
            WITHOUT
          </h2>
          <p className="text-[#FF6B35] font-bold tracking-wide text-sm md:text-base" data-id="element-16">
            WeddingPitch
          </p>
        </div>

        {/* Chaotic Reviews */}
        <ReviewCard variant="bad" rating={1} text="Terrible communication. Showed up 2 hours late! 😡" className="absolute top-[28%] left-[6%] -rotate-6" data-id="element-17" />
        <ReviewCard variant="bad" rating={2} text="Very stressful experience. Would not recommend." className="absolute top-[50%] left-[18%] rotate-3" data-id="element-18" />
        <ReviewCard variant="bad" rating={1} text="Never again. Ruined our timeline completely. 😫" className="absolute top-[72%] left-[4%] -rotate-3" data-id="element-19" />

        {/* Floating Emojis */}
        <span className="absolute top-[22%] left-[45%] text-2xl opacity-60 -rotate-12" data-id="element-20">
          😤
        </span>
        <span className="absolute top-[45%] left-[8%] text-xl opacity-50 rotate-12" data-id="element-21">
          📉
        </span>
        <span className="absolute top-[65%] left-[42%] text-2xl opacity-40 rotate-6" data-id="element-22">
          😫
        </span>
      </div>

      {/* RIGHT SIDE: WITH WeddingPitch */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_60%,rgba(78,205,196,0.2),transparent_60%)]" style={{
      clipPath: 'polygon(55% 0, 100% 0, 100% 100%, 45% 100%)'
    }} data-id="element-23">
        {/* Confident Silhouette */}
        <svg viewBox="0 0 100 100" className="absolute bottom-16 -right-4 w-64 h-64 opacity-10 text-[#4ECDC4] fill-current" data-id="element-24">
          <circle cx="65" cy="25" r="12" data-id="element-25" />
          <path d="M65 40 C80 40 95 70 100 100 L30 100 C35 70 50 40 65 40 Z" data-id="element-26" />
          <path d="M45 55 L35 70 L45 85" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" data-id="element-27" />
          <path d="M85 55 L95 70 L85 85" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" data-id="element-28" />
        </svg>

        <div className="absolute top-8 right-8 text-right" data-id="element-29">
          <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-1" data-id="element-30">
            WITH
          </h2>
          <p className="text-[#4ECDC4] font-bold tracking-wide text-sm md:text-base" data-id="element-31">
            WeddingPitch
          </p>
        </div>

        {/* Organized Reviews */}
        <div className="absolute top-[28%] right-[8%] flex flex-col gap-5" data-id="element-32">
          <ReviewCard variant="good" rating={5} text="Absolutely perfect! Made our day so special. ✨" data-id="element-33" />
          <ReviewCard variant="good" rating={5} text="Highly recommend! The best vendor we hired." data-id="element-34" />
          <ReviewCard variant="good" rating={5} text="Flawless execution and amazing service! 🎉" data-id="element-35" />
        </div>

        {/* Floating Emojis */}
        <span className="absolute top-[22%] right-[45%] text-2xl opacity-80 rotate-12" data-id="element-36">
          😊
        </span>
        <span className="absolute top-[48%] right-[52%] text-xl opacity-60 -rotate-6" data-id="element-37">
          ⭐
        </span>
        <span className="absolute top-[75%] right-[48%] text-2xl opacity-70 rotate-6" data-id="element-38">
          📈
        </span>
      </div>

      {/* DIAGONAL DIVIDER LINE */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" data-id="element-39">
        <line x1="55%" y1="0" x2="45%" y2="100%" stroke="white" strokeWidth="3" className="opacity-20" data-id="element-40" />
        {/* Glow effect for the line */}
        <line x1="55%" y1="0" x2="45%" y2="100%" stroke="white" strokeWidth="10" className="opacity-5 blur-sm" data-id="element-41" />
      </svg>

      {/* BOTTOM BAR */}
      <div className="absolute bottom-0 left-0 right-0 h-[12%] min-h-[64px] bg-[#0a0a14]/95 backdrop-blur-md border-t border-white/10 flex items-center justify-between px-6 z-20" data-id="element-42">
        <div className="flex items-center gap-1" data-id="element-43">
          <span className="text-white font-black text-lg tracking-tight" data-id="element-44">
            Wedding
          </span>
          <span className="text-[#4ECDC4] font-black text-lg tracking-tight" data-id="element-45">
            Pitch
          </span>
        </div>

        <div className="bg-gradient-to-r from-[#FF6B35] to-[#4ECDC4] p-[2px] rounded-full shadow-lg" data-id="element-46">
          <div className="bg-[#0a0a14] rounded-full px-4 py-1.5 h-full flex items-center justify-center" data-id="element-47">
            <span className="bg-gradient-to-r from-[#FF6B35] to-[#4ECDC4] bg-clip-text text-transparent font-bold text-xs md:text-sm whitespace-nowrap" data-id="element-48">
              Start Managing Your Reviews Today
            </span>
          </div>
        </div>
      </div>
    </div>;
}